import {
  ChangeDetectionStrategy,
  Component,
  computed,
  type ElementRef,
  effect,
  inject,
  viewChild,
  untracked,
} from '@angular/core';

import { I18nPipe } from '@core/i18n';
import { NavStore } from '@core/nav';

import { ButtonComponent, IconButtonComponent } from '@shared/button';
import { ToastService } from '@shared/toast';
import { TooltipDirective } from '@shared/tooltip';

@Component({
  selector: 'ks-tabbar',
  imports: [I18nPipe, IconButtonComponent, ButtonComponent, TooltipDirective],
  host: { '(keydown)': 'onKeydown($event)' },
  templateUrl: './tabbar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabbarComponent {
  private readonly tablistRef = viewChild.required<ElementRef<HTMLElement>>('tablist');
  private readonly navStore = inject(NavStore);
  private readonly toast = inject(ToastService);

  protected readonly tabs = this.navStore.tabs;
  protected readonly activeTabId = this.navStore.activeTabId;
  protected readonly tabLimitAttempts = this.navStore.tabLimitAttempts;

  protected readonly showLeftArrow = computed(
    () => this.tabs().findIndex((t) => t.id === this.activeTabId()) > 0,
  );
  protected readonly showRightArrow = computed(() => {
    const tabs = this.tabs();
    const activeIndex = tabs.findIndex((t) => t.id === this.activeTabId());

    return activeIndex >= 0 && activeIndex < tabs.length - 1;
  });

  constructor() {
    effect(() => {
      this.tabs();
      this.activeTabId();

      queueMicrotask(() => this.scrollToActiveTab());
    });

    let handledLimitAttempt = 0;

    effect(() => {
      const attempt = this.tabLimitAttempts();

      if (!attempt || attempt === handledLimitAttempt) return;

      handledLimitAttempt = attempt;

      untracked(() =>
        this.toast.warn({ title: 'tab.maxTabsTitle', description: 'tab.maxTabsDescription' }),
      );
    });
  }

  protected activateAdjacentTab(offset: -1 | 1): void {
    const tabs = this.tabs();
    const activeIndex = tabs.findIndex((t) => t.id === this.activeTabId());
    const adjacentTab = tabs[activeIndex + offset];

    if (adjacentTab) this.activateTab(adjacentTab.id);
  }

  protected activateTab(tabId: string): void {
    this.navStore.activateTab(tabId);
  }

  protected closeTab(event: Event, tabId: string): void {
    event.stopPropagation();
    this.navStore.closeTab(tabId);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.repeat) return;

    const tabs = this.tabs();
    const activeTabId = this.activeTabId();
    const currentIndex = tabs.findIndex((t) => t.id === activeTabId);

    if (currentIndex === -1) return;

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
        break;
      case 'ArrowRight':
        event.preventDefault();
        nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        event.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    const nextTab = tabs[nextIndex];

    if (nextTab) {
      this.activateTab(nextTab.id);
    }
  }

  private scrollToActiveTab(): void {
    const el = this.tablistRef().nativeElement;
    const activeBtn = el.querySelector('[role="tab"][aria-selected="true"]');

    activeBtn?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }
}
